import TagManager from "react-gtm-module";

type DataLayerEvent = {
  event?: string;
  [key: string]: unknown;
};

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
  }
}

export type TagsItem = {
  item_id: string | number;
  item_name: string;
  item_category?: string;
  affiliation?: string;
  price?: number;
  quantity?: number;
};

export enum TagsEvent {
  PageView = "page_view",
  SetUser = "set_user",
  Login = "login",
  Logout = "logout",
  ChangeLanguage = "change_language",

  ViewItemList = "view_item_list",
  SelectItem = "select_item",
  ViewItem = "view_item",
  BeginCheckout = "begin_checkout",
  AddPaymentInfo = "add_payment_info",
  Purchase = "purchase",
  BookingCompleted = "booking_completed",

  LoginSubmitted = "login_submitted",
  AcceptTerms = "accept_terms",
  VerifyOtp = "verify_otp",
  OtpFailed = "otp_failed",
  ResendOtp = "resend_otp",
  EditEmail = "edit_email",

  ReviewAndBook = "review_and_book",
  EditBookingDetails = "edit_booking_details",
  PlaceOrder = "place_order",
  AcceptBookingTerms = "accept_booking_terms",
  AdditionalServiceApproval = "additional_service_approval",
  PrintConfirmation = "print_confirmation",

  SelectContent = "select_content",
  EditBookingForm = "edit_booking_form",
  ApplyFilter = "apply_filter",
  SortList = "sort_list",
  SearchList = "search_list",
  PaginateList = "paginate_list",

  SocialClick = "social_click",
}

export enum TagsContentType {
  NavLink = "nav_link",
  FooterLink = "footer_link",
  LegalLink = "legal_link",
  MapCoverage = "map_coverage",
  BookingDetail = "booking_detail",
  StartNewBooking = "start_new_booking",
  NextAvailable = "next_available",
}

export enum TagsNextAvailableTarget {
  SlotsCount = "slots_count",
}

export enum TagsFields {
  BookingDate = "booking_date",
  CustomDatePickerOpen = "custom_date_picker_open",
  PeopleCount = "people_count",
  NotifyMember = "notify_member",
  Slot = "slot",
  Place = "place",
  ServiceTypeId = "serviceTypeId",
  Language = "language",
  DateRange = "dateRange",
  Radius = "radius",
  SearchString = "searchString",
  OrderBy = "orderBy",
  SortBy = "sortBy",
  BookerFirstName = "booker_first_name",
  BookerLastName = "booker_last_name",
  BookerEmail = "booker_email",
  BillingFirstName = "billing_first_name",
  BillingLastName = "billing_last_name",
  BillingEmail = "billing_email",
  BillingPhone = "billing_phone",
}

/** Human-readable date-chip categories for `edit_booking_form`. */
export enum TagsDateCategory {
  Today = "Today",
  Tomorrow = "Tomorrow",
  ThisWeek = "this week",
  Custom = "Custom",
}

export enum TagsMode {
  Page = "page",
  PageSize = "page_size",
  LoadMore = "load_more",
}

export enum TagsId {
  ContactUs = "contact_us",
  SendFeedback = "send_feedback",
  TermsOfUse = "terms_of_use",
  PrivacyPolicy = "privacy_policy",
}

const CURRENCY = "EUR";
let initialized = false;

/** Keys that otherwise stick across dataLayer pushes and pollute later events. */
const CLEAR_KEYS = [
  "value",
  "label",
  "category",
  "field",
  "filter_type",
  "content_type",
  "item_id",
  "target",
  "slots_count",
  "screen",
  "mode",
  "prefilled",
  "sort_by",
  "language",
  "platform",
  "enabled",
  "status",
  "order_id",
  "page",
  "page_size",
  "booking_id",
  "country",
  "city",
  "service",
  "method",
  "page_path",
  "page_title",
] as const;

function push(payload: DataLayerEvent): void {
  try {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push(payload);
  } catch {}
}

function init(userId?: string | null): void {
  if (initialized) return;
  const gtmId = process.env.REACT_APP_GTM_ID;
  if (!gtmId) return;
  try {
    TagManager.initialize({
      gtmId,
      ...(userId ? { dataLayer: { user_id: userId } } : {}),
    });
    initialized = true;
  } catch {}
}

/** Push a GA4/GTM event. DataLayer keys stay as-is for GTM. */
function event(name: TagsEvent, params?: Record<string, unknown>): void {
  if (params?.ecommerce) push({ ecommerce: null });
  const cleared = Object.fromEntries(
    CLEAR_KEYS.filter((key) => !(params && key in params)).map((key) => [
      key,
      null,
    ]),
  );
  push({ ...cleared, event: name, ...params });
}

function setUserId(userId: string | null): void {
  push({ event: TagsEvent.SetUser, user_id: userId ?? undefined });
}

function buildEcommerce(items: TagsItem[], extra?: Record<string, unknown>) {
  const { value, ...rest } = extra ?? {};
  const total = items.reduce(
    (sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1),
    0,
  );
  return {
    currency: CURRENCY,
    value: (value as number | undefined) ?? total,
    items,
    ...rest,
  };
}

function ecommerce(
  name: TagsEvent,
  items: TagsItem[],
  extra?: Record<string, unknown>,
): void {
  if (!items.length) return;
  event(name, { ecommerce: buildEcommerce(items, extra) });
}

const Tags = {
  init,
  event,
  ecommerce,
  setUserId,
  buildEcommerce,
};

export default Tags;
